"use client";

import { useCallback } from "react";
import { SafeEmailBody as SharedSafeEmailBody } from "@wtc/email/react";
import type { PrepareMarkupOptions } from "@wtc/email/react";
import { cn } from "@/lib/utils";
import { prepareEmailBodyMarkupForDomPurify } from "@/lib/email/safe-email-body-utils";

/**
 * Hub's email-body renderer. Rendering, sanitization, and the leaving-your-inbox
 * link interstitial all live in the shared `@wtc/email/react` component (single
 * source, identical across Hub and the WTC mail client). Hub supplies two things:
 *
 * 1. its CRM-specific pre-sanitize normalization —
 *    {@link prepareEmailBodyMarkupForDomPurify} (entity-decoding, Proofpoint footer
 *    stripping, multipart dedup, plaintext linkify, timeline snippets) — via the
 *    shared component's `prepareMarkup` hook; and
 * 2. its own wrapper styling (timeline border for `full`, gradient-fade mask for
 *    `snippet`) via `className` / `proseClassName`, in Hub's design tokens.
 */

export interface SafeEmailBodyProps {
  body: string;
  variant?: "full" | "snippet";
  className?: string;
  /** Extra class on the framing wrapper (limited effect with iframe rendering). */
  proseClassName?: string;
  /**
   * Timeline rows stop link clicks from toggling the row. With iframe rendering
   * interaction stays inside the frame, so this is a no-op — kept for API stability.
   */
  stopLinkClickPropagation?: boolean;
  snippetMaxChars?: number;
  /**
   * Collapse the quoted-reply / forwarded trailer behind a "show quoted text"
   * toggle (Gmail/Outlook behavior). Only affects `variant="full"`. Default off.
   */
  collapseQuotedReplies?: boolean;
}

export function SafeEmailBody({
  body,
  variant = "full",
  className,
  proseClassName,
  stopLinkClickPropagation: _stopLinkClickPropagation,
  snippetMaxChars = 300,
  collapseQuotedReplies = false,
}: SafeEmailBodyProps) {
  const prepareMarkup = useCallback(
    (raw: string, options: PrepareMarkupOptions): string =>
      prepareEmailBodyMarkupForDomPurify(raw, options).rawMarkupForPurify,
    [],
  );

  const frameClassName =
    variant === "snippet"
      ? cn(
          "relative max-h-[4.85rem] overflow-hidden rounded-md border border-border/60 bg-muted/20",
          "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-[1] after:h-9 after:bg-gradient-to-t after:from-background after:to-transparent",
          proseClassName,
        )
      : cn("rounded-md border border-border/60 bg-muted/10", proseClassName);

  return (
    <SharedSafeEmailBody
      body={body}
      variant={variant}
      className={cn("min-h-0 min-w-0", className)}
      proseClassName={frameClassName}
      snippetMaxChars={snippetMaxChars}
      prepareMarkup={prepareMarkup}
      collapseQuotedReplies={collapseQuotedReplies}
    />
  );
}
